import type { CelebrationStyle } from '@goaltree/backend/convex/lib/celebration'

// Check-off celebrations: callers emit a point (or a big full-screen moment);
// CelebrationLayer draws the user's chosen style there.
export type CelebrateEvent = { x: number; y: number; big?: boolean; style?: CelebrationStyle }

const EVENT = 'goaltree:celebrate'

export function celebrate(e: CelebrateEvent) {
  window.dispatchEvent(new CustomEvent<CelebrateEvent>(EVENT, { detail: e }))
}

// Celebrate from an element's centre (the checkbox or flame that was tapped).
export function celebrateFrom(el: Element | null | undefined, big = false) {
  if (!el) return
  const r = el.getBoundingClientRect()
  celebrate({ x: r.left + r.width / 2, y: r.top + r.height / 2, big })
}

export function onCelebrate(cb: (e: CelebrateEvent) => void) {
  const h = (e: Event) => cb((e as CustomEvent<CelebrateEvent>).detail)
  window.addEventListener(EVENT, h)
  return () => window.removeEventListener(EVENT, h)
}

// ── Sounds: synthesised with Web Audio (no files), each under ~0.5s. ──────────

let audio: AudioContext | null = null
function ctx() {
  try {
    audio ??= new AudioContext()
    if (audio.state === 'suspended') void audio.resume()
    return audio
  } catch {
    return null
  }
}

function noise(ac: AudioContext, seconds: number) {
  const buf = ac.createBuffer(1, Math.ceil(ac.sampleRate * seconds), ac.sampleRate)
  const data = buf.getChannelData(0)
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
  const src = ac.createBufferSource()
  src.buffer = buf
  return src
}

function envelope(ac: AudioContext, at: number, peak: number, attack: number, release: number) {
  const g = ac.createGain()
  g.gain.setValueAtTime(0.0001, at)
  g.gain.exponentialRampToValueAtTime(peak, at + attack)
  g.gain.exponentialRampToValueAtTime(0.0001, at + attack + release)
  return g
}

function tone(
  ac: AudioContext,
  freq: number,
  at: number,
  dur: number,
  peak = 0.12,
  type: OscillatorType = 'sine',
) {
  const o = ac.createOscillator()
  o.type = type
  o.frequency.setValueAtTime(freq, at)
  const g = envelope(ac, at, peak, 0.01, dur)
  o.connect(g).connect(ac.destination)
  o.start(at)
  o.stop(at + dur + 0.05)
  return o
}

function filteredNoise(
  ac: AudioContext,
  at: number,
  dur: number,
  type: BiquadFilterType,
  from: number,
  to: number,
  peak: number,
) {
  const src = noise(ac, dur + 0.05)
  const f = ac.createBiquadFilter()
  f.type = type
  f.frequency.setValueAtTime(from, at)
  f.frequency.exponentialRampToValueAtTime(to, at + dur)
  const g = envelope(ac, at, peak, 0.008, dur)
  src.connect(f).connect(g).connect(ac.destination)
  src.start(at)
  src.stop(at + dur + 0.05)
}

export function playCelebrationSound(style: CelebrationStyle, big = false) {
  const ac = ctx()
  if (!ac) return
  const t = ac.currentTime + 0.01
  const vol = big ? 1.4 : 1
  switch (style) {
    case 'classic': {
      const o = tone(ac, 520, t, 0.12, 0.1 * vol)
      o.frequency.exponentialRampToValueAtTime(880, t + 0.08)
      break
    }
    case 'burst':
      filteredNoise(ac, t, 0.18, 'lowpass', 3000, 200, 0.5 * vol)
      tone(ac, 1200, t + 0.02, 0.1, 0.06 * vol, 'triangle')
      if (big) filteredNoise(ac, t + 0.15, 0.3, 'lowpass', 2000, 120, 0.4)
      break
    case 'flame':
      filteredNoise(ac, t, big ? 0.6 : 0.38, 'bandpass', 300, 2400, 0.35 * vol)
      break
    case 'applause':
      for (let i = 0; i < (big ? 14 : 6); i++)
        filteredNoise(
          ac,
          t + i * 0.055 + Math.random() * 0.03,
          0.05,
          'highpass',
          1500,
          1200,
          0.35 * vol,
        )
      break
    case 'hearts':
      tone(ac, 1318.5, t, 0.25, 0.08 * vol)
      tone(ac, 1661.2, t + 0.09, 0.3, 0.07 * vol)
      if (big) tone(ac, 1975.5, t + 0.18, 0.4, 0.06)
      break
    case 'surprise':
      break
  }
}
